import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import ChatThread from "@/components/chat/ChatThread";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Hash, Lock, MessageSquare, FileText, Building2, Plus, Loader2, ArrowLeft } from "lucide-react";

function Avatar({ name, src, size = "h-7 w-7" }) {
  if (src) return <img src={src} alt="" className={`${size} shrink-0 rounded-full object-cover`} />;
  return <div className={`${size} flex shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-bold uppercase`}>{(name || "U").slice(0, 2)}</div>;
}

function convGlyph(c) {
  if (c.type === "channel") return c.admin_only ? <Lock className="h-4 w-4" /> : <Hash className="h-4 w-4" />;
  if (c.type === "dm") return <Avatar name={c.title} src={c.avatar} size="h-7 w-7" />;
  if (c.type === "quote") return <FileText className="h-4 w-4" />;
  return <Building2 className="h-4 w-4" />;
}

function ConvItem({ c, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`conv-${c.id}`}
      className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition ${active ? "bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]" : "hover:bg-secondary"}`}
    >
      <span className={`flex h-7 w-7 items-center justify-center ${c.type === "dm" ? "" : "rounded-md bg-secondary text-muted-foreground"}`}>{convGlyph(c)}</span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1">
          <span className="truncate text-sm font-semibold">{c.title}</span>
        </span>
        {c.last_text ? (
          <span className="block truncate text-xs text-muted-foreground">{c.last_sender_name ? `${c.last_sender_name.split(" ")[0]}: ` : ""}{c.last_text}</span>
        ) : c.subtitle ? (
          <span className="block truncate text-xs text-muted-foreground">{c.subtitle}</span>
        ) : null}
      </span>
      {c.unread > 0 && (
        <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[hsl(var(--primary))] px-1.5 text-[11px] font-bold text-white" data-testid={`conv-unread-${c.id}`}>{c.unread}</span>
      )}
    </button>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const { users, refreshUnread } = useChat();
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [showThreadMobile, setShowThreadMobile] = useState(false);

  const loadConvs = (pick) => {
    api.get("/chat/conversations")
      .then((r) => {
        const list = r.data || [];
        setConvs(list);
        setSelected((cur) => {
          if (pick) return list.find((c) => c.id === pick) || pick;
          if (cur) return list.find((c) => c.id === cur.id) || cur;
          return list.find((c) => c.type === "channel") || list[0] || null;
        });
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadConvs();
    const t = setInterval(loadConvs, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = (c) => { setSelected(c); setShowThreadMobile(true); setTimeout(() => loadConvs(c.id), 400); };

  const startDm = (u) => {
    api.post("/chat/dm", { user_id: u.id })
      .then((r) => { setNewOpen(false); setUserQuery(""); loadConvs(r.data.id); setSelected(r.data); setShowThreadMobile(true); })
      .catch((e) => toast.error(apiError(e)));
  };

  const groups = useMemo(() => ({
    channels: convs.filter((c) => c.type === "channel"),
    dms: convs.filter((c) => c.type === "dm"),
    threads: convs.filter((c) => c.type === "quote" || c.type === "customer"),
  }), [convs]);

  const dmCandidates = users.filter((u) => u.id !== user?.id && (u.name || "").toLowerCase().includes(userQuery.toLowerCase()));

  return (
    <div className="flex h-[calc(100vh-7rem)] gap-4 animate-fade-up" data-testid="messages-page">
      {/* conversation list */}
      <aside className={`${showThreadMobile ? "hidden" : "flex"} w-full flex-col rounded-lg border border-border bg-card lg:flex lg:w-72`}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h1 className="font-heading text-lg font-black tracking-tight">Messages</h1>
          <Button size="sm" variant="outline" onClick={() => setNewOpen(true)} data-testid="new-dm-btn"><Plus className="mr-1 h-4 w-4" />DM</Button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-2">
          {loading && <div className="flex h-32 items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>}
          {!loading && (
            <>
              <Section label="Channels">
                {groups.channels.map((c) => <ConvItem key={c.id} c={c} active={selected?.id === c.id} onClick={() => select(c)} />)}
              </Section>
              <Section label="Direct Messages" empty="No DMs yet — start one with the DM button.">
                {groups.dms.map((c) => <ConvItem key={c.id} c={c} active={selected?.id === c.id} onClick={() => select(c)} />)}
              </Section>
              {groups.threads.length > 0 && (
                <Section label="Quote & Customer Threads">
                  {groups.threads.map((c) => <ConvItem key={c.id} c={c} active={selected?.id === c.id} onClick={() => select(c)} />)}
                </Section>
              )}
            </>
          )}
        </div>
      </aside>

      {/* active thread */}
      <section className={`${showThreadMobile ? "flex" : "hidden"} min-w-0 flex-1 flex-col rounded-lg border border-border bg-card lg:flex`}>
        {selected ? (
          <>
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <button className="lg:hidden" onClick={() => setShowThreadMobile(false)}><ArrowLeft className="h-5 w-5" /></button>
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary text-muted-foreground">{convGlyph(selected)}</span>
              <div className="min-w-0">
                <div className="truncate font-heading text-base font-bold">{selected.title}</div>
                {selected.subtitle && <div className="truncate text-xs text-muted-foreground">{selected.subtitle}</div>}
              </div>
            </div>
            <ChatThread
              key={selected.id}
              conversationId={selected.id}
              heightClass="flex-1"
              onActivity={() => loadConvs(selected.id)}
            />
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <div className="text-center"><MessageSquare className="mx-auto mb-2 h-8 w-8 opacity-40" />Select a conversation</div>
          </div>
        )}
      </section>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-dm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New direct message</DialogTitle>
            <DialogDescription>Pick a teammate to start a private conversation.</DialogDescription>
          </DialogHeader>
          <Input autoFocus value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder="Search teammates…" data-testid="new-dm-search" />
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {dmCandidates.map((u) => (
              <button key={u.id} onClick={() => startDm(u)} data-testid={`dm-user-${u.id}`} className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-secondary">
                <Avatar name={u.name} src={u.avatar} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{u.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{u.title || u.email}</span>
                </span>
                <span className="text-[10px] uppercase text-muted-foreground">{u.role === "admin" ? "admin" : "rep"}</span>
              </button>
            ))}
            {dmCandidates.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No teammates found.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ label, children, empty }) {
  const arr = Array.isArray(children) ? children.filter(Boolean) : children ? [children] : [];
  return (
    <div>
      <div className="label-caps px-2 pb-1 pt-1">{label}</div>
      {arr.length ? <div className="space-y-0.5">{children}</div> : empty ? <div className="px-2 py-1 text-xs text-muted-foreground/70">{empty}</div> : null}
    </div>
  );
}
